// frontend/src/components/routines/SkillPickerModal.js
import React, { useState, useMemo, useCallback } from 'react';
import Toast from '../Toast';
import { rankSkills, sortByDifficulty, normalizeFig } from '../../utils/skillSearch';
import { groupShapeVariants, figStem } from '../../utils/skillGroups';
import { isFeetToFeet } from '../../utils/skillPositions';
import useVisibleViewport from '../../hooks/useVisibleViewport';
import './SkillPicker.css';

const SHAPE_WORD = { T: 'tuck', P: 'pike', S: 'straight' };
const dd = (d) => (d != null && d !== '' ? Number(d).toFixed(1) : null);

// Only take focus straight away with a mouse or trackpad. On a phone the
// keyboard would cover the results before the coach has seen them.
const prefersAutoFocus = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: fine)').matches;

// Search the club's skill library and pick a tracked skill. With allowCustom
// (the training diary only), the typed text can also be added as a custom
// skill with optional FIG notation and difficulty; routines use the library only.
// Calls onSave(skillId) for a tracked skill, or
// onSave(null, name, { figNotation, difficulty }) for a custom one.
// In add mode the picker stays open so a coach can add several skills in a
// row; each add shows an "Added" toast. onSave may return a promise, and a
// result of false (or a rejection) shows an error toast instead. In replace
// mode the parent closes the picker as before.
// apparatus narrows the list, with a toggle to show the rest: Double Mini
// lists skills with a DMT difficulty (and shows that difficulty), Tumbling
// lists skills that start and land on the feet.
const SkillPickerModal = ({ availableSkills, onSave, onCancel, mode = 'add', apparatus = 'TRA', allowCustom = false }) => {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customFig, setCustomFig] = useState('');
  const [customDiff, setCustomDiff] = useState('');
  const [autoFocus] = useState(prefersAutoFocus);
  const viewportStyle = useVisibleViewport();
  const [toast, setToast] = useState(null);
  const dismissToast = useCallback(() => setToast(null), []);
  const isAdd = mode !== 'replace';

  const pick = (label, ...args) => {
    const result = onSave(...args);
    if (!isAdd) return;
    Promise.resolve(result).then(
      (ok) => setToast(ok === false
        ? { id: Date.now(), type: 'error', message: `Could not add ${label}` }
        : { id: Date.now(), type: 'success', message: `Added ${label}` }),
      () => setToast({ id: Date.now(), type: 'error', message: `Could not add ${label}` }),
    );
  };

  const narrows = apparatus === 'DMT' || apparatus === 'TUM';
  const filtering = narrows && !showAll;
  const groups = useMemo(() => {
    let pool = availableSkills;
    if (apparatus === 'DMT') {
      // Show, and sort by, double mini difficulty
      pool = pool.map(s => ({ ...s, difficulty: s.dmtDifficulty }));
      if (filtering) pool = pool.filter(s => Number(s.difficulty) > 0);
    } else if (apparatus === 'TUM' && filtering) {
      pool = pool.filter(isFeetToFeet);
    }
    const matched = query.trim() ? rankSkills(query, pool) : sortByDifficulty(pool);
    return groupShapeVariants(matched);
  }, [query, availableSkills, filtering, apparatus]);

  const typed = query.trim();
  const showCustomForm = allowCustom && Boolean(typed) && (customOpen || groups.length === 0);
  const title = mode === 'replace' ? 'Replace skill' : 'Add skill';

  const addCustom = () => {
    if (!typed) return;
    pick(typed, null, typed, {
      figNotation: normalizeFig(customFig) || null,
      difficulty: customDiff === '' ? null : Number(customDiff),
    });
    if (isAdd) {
      setQuery('');
      setCustomOpen(false);
      setCustomFig('');
      setCustomDiff('');
    }
  };

  return (
    <div className="modal-overlay skill-picker__overlay" style={viewportStyle}>
      <div className="modal skill-picker" role="dialog" aria-labelledby="skill-picker-title">
        <div className="skill-picker__header">
          <h3 id="skill-picker-title">{title}</h3>
          {isAdd
            ? <button type="button" className="skill-picker__done" onClick={onCancel}>Done</button>
            : <button type="button" className="skill-picker__close" onClick={onCancel} aria-label="Close">×</button>}
        </div>

        <input
          type="search"
          className="skill-picker__search"
          aria-label="Search skills"
          placeholder="Search by name or FIG, e.g. barani or 41/"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setCustomOpen(false); }}
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
        />

        {narrows && (
          <label className="skill-picker__filter">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            {apparatus === 'DMT'
              ? 'Include skills with no double mini difficulty'
              : 'Include skills that start or land off the feet'}
          </label>
        )}

        <ul className="skill-picker__results">
          {groups.map(group => {
            const first = group.variants[0];
            if (!first.shape) {
              const s = first.skill;
              return (
                <li key={group.key}>
                  <button type="button" className="skill-picker__row" onClick={() => pick(s.name, s.id)}>
                    <span className="skill-picker__name">{s.name}</span>
                    <span className="skill-picker__meta">
                      {s.figNotation && <code>{s.figNotation}</code>}
                      {dd(s.difficulty) && <span>DD {dd(s.difficulty)}</span>}
                    </span>
                  </button>
                </li>
              );
            }
            const stem = figStem(first.skill.figNotation);
            return (
              <li key={group.key} className="skill-picker__group">
                <div className="skill-picker__group-text">
                  <span className="skill-picker__name">{group.base}</span>
                  {stem && <code className="skill-picker__meta">{stem}</code>}
                </div>
                <div className="skill-picker__shapes">
                  {group.variants.map(({ shape, skill }) => (
                    <button
                      key={skill.id}
                      type="button"
                      className="skill-picker__shape"
                      onClick={() => pick(skill.name, skill.id)}
                      aria-label={`${group.base}, ${SHAPE_WORD[shape]}${dd(skill.difficulty) ? `, DD ${dd(skill.difficulty)}` : ''}`}
                      title={skill.figNotation || undefined}
                    >
                      <strong>{shape}</strong>
                      {dd(skill.difficulty) && <span>{dd(skill.difficulty)}</span>}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}

          {groups.length === 0 && !typed && (
            <li className="skill-picker__empty">{filtering
              ? (apparatus === 'DMT' ? 'No skills with a double mini difficulty in the library.' : 'No feet-to-feet skills in the library.')
              : 'No skills in the library yet.'}</li>
          )}

          {typed && !allowCustom && groups.length === 0 && (
            <li className="skill-picker__empty">No skill called "{typed}" in the library. A club admin can add it on the Skills page.</li>
          )}

          {allowCustom && typed && !showCustomForm && (
            <li>
              <button type="button" className="skill-picker__row skill-picker__custom-toggle" onClick={() => setCustomOpen(true)}>
                + Add "{typed}" as a custom skill…
              </button>
            </li>
          )}

          {showCustomForm && (
            <li className="skill-picker__custom">
              {groups.length === 0 && <p className="skill-picker__empty">No matches. Add "{typed}" as a custom skill:</p>}
              <div className="skill-picker__custom-fields">
                <label>
                  FIG (optional)
                  <input
                    type="text"
                    aria-label="Custom FIG notation"
                    value={customFig}
                    onChange={(e) => setCustomFig(e.target.value)}
                    autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
                  />
                </label>
                <label>
                  DD (optional)
                  <input
                    type="number" step="0.1" min="0" inputMode="decimal"
                    aria-label="Custom difficulty"
                    value={customDiff}
                    onChange={(e) => setCustomDiff(e.target.value)}
                  />
                </label>
              </div>
              <button type="button" className="btn btn-primary" onClick={addCustom}>Add custom skill</button>
            </li>
          )}
        </ul>
        {toast && <Toast key={toast.id} message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </div>
    </div>
  );
};

export default SkillPickerModal;
